import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { CheckCircle, ArrowRight, SpinnerGap } from "@phosphor-icons/react";

const SAM_OPTIONS = [
  { value: "registered", label: "Registered and active" },
  { value: "in_progress", label: "Started, not finished" },
  { value: "not_started", label: "Not started" },
  { value: "not_sure", label: "Not sure" },
];

const GOAL_OPTIONS = [
  { value: "federal_contracts", label: "Win federal contracts" },
  { value: "local_bids", label: "City and county bids" },
  { value: "state_contracts", label: "South Carolina state contracts" },
  { value: "certifications", label: "Get certified (WOSB, HUBZone, etc.)" },
  { value: "not_sure", label: "Not sure yet" },
];

const EMPTY = {
  name: "", business_name: "", phone: "", email: "", business_description: "",
  sam_status: "", main_goal: "", best_time: "", referred_by: "",
};

const C = { navy: "#0B1F3A", cream: "#F5F1E8", brass: "#B8893B", ink: "#1C2430" };

const ChoiceGroup = ({ name, options, value, onChange }) => (
  <div className="grid sm:grid-cols-2 gap-2" role="radiogroup">
    {options.map((o) => {
      const active = value === o.value;
      return (
        <button
          type="button"
          key={o.value}
          role="radio"
          aria-checked={active}
          data-testid={`fcs-${name}-${o.value}`}
          onClick={() => onChange(o.value)}
          className="text-left px-4 py-3 border text-sm font-medium transition-colors duration-200"
          style={{
            borderColor: active ? C.navy : "#D8D1C2",
            background: active ? C.navy : "#FFFDF8",
            color: active ? C.cream : C.ink,
          }}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

const Field = ({ id, label, hint, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-[13px] font-semibold tracking-wide" style={{ color: C.ink }}>
      {label}
    </Label>
    {children}
    {hint && <p className="text-xs" style={{ color: "#6B6457" }}>{hint}</p>}
  </div>
);

const SuccessPanel = ({ name, onReset }) => (
  <div className="py-16 fcs-rise" data-testid="fcs-intake-success">
    <CheckCircle size={48} weight="duotone" style={{ color: C.brass }} />
    <h2 className="mt-6 text-3xl font-extrabold" style={{ color: C.navy }}>Thank you, {name.split(" ")[0]}.</h2>
    <p className="mt-3 text-base max-w-md" style={{ color: "#4A4438" }}>
      Your information is in. Expect a call or text at the time you picked to set up a free 15-minute conversation.
    </p>
    <button type="button" onClick={onReset} data-testid="fcs-intake-reset" className="mt-8 text-sm underline" style={{ color: C.navy }}>
      Submit another business
    </button>
  </div>
);

export default function FcsIntake() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [doneName, setDoneName] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const inputCls = "rounded-none h-11 bg-[#FFFDF8] border-[#D8D1C2] focus-visible:ring-[#0B1F3A] text-[#1C2430]";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.sam_status || !form.main_goal) {
      setError("Please choose your SAM.gov status and your main goal.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/fcs/intake`, form);
      setDoneName(form.name);
      setForm(EMPTY);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: C.cream, fontFamily: "Manrope, sans-serif" }} data-testid="fcs-intake-page">
      <style>{`.fcs-rise{animation:fcsRise .6s ease-out both}@keyframes fcsRise{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}`}</style>
      <div className="grid lg:grid-cols-[5fr_7fr] min-h-screen">
        <aside className="px-8 sm:px-12 py-12 lg:py-20 flex flex-col justify-between" style={{ background: C.navy, color: C.cream }}>
          <div className="fcs-rise">
            <p className="font-mono text-xs tracking-[0.25em] uppercase" style={{ color: C.brass }} data-testid="fcs-brand">
              Federal Contract Sprints
            </p>
            <h1 className="mt-8 text-4xl sm:text-5xl font-extrabold leading-[1.05]">
              Get your business ready to sell to the government.
            </h1>
            <p className="mt-6 text-base max-w-sm opacity-80">
              Tell us a little about your business. We'll review it and reach out to set up a free 15-minute call.
            </p>
          </div>
          <ul className="mt-12 space-y-4 text-sm opacity-90 fcs-rise" style={{ animationDelay: ".15s" }}>
            {["Takes about 2 minutes", "No cost, no obligation", "Your information is only used to contact you"].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <span className="w-6 h-px" style={{ background: C.brass }} />
                {t}
              </li>
            ))}
          </ul>
        </aside>

        <main className="px-6 sm:px-12 lg:px-20 py-12 lg:py-20">
          {doneName ? (
            <SuccessPanel name={doneName} onReset={() => setDoneName("")} />
          ) : (
            <form onSubmit={submit} className="max-w-2xl mx-auto lg:mx-0 xl:mx-auto space-y-8 fcs-rise" style={{ animationDelay: ".1s" }} data-testid="fcs-intake-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <Field id="name" label="Your name *">
                  <Input id="name" required value={form.name} onChange={set("name")} className={inputCls} data-testid="fcs-input-name" />
                </Field>
                <Field id="business_name" label="Business name *">
                  <Input id="business_name" required value={form.business_name} onChange={set("business_name")} className={inputCls} data-testid="fcs-input-business-name" />
                </Field>
                <Field id="phone" label="Phone *">
                  <Input id="phone" type="tel" required value={form.phone} onChange={set("phone")} className={inputCls} data-testid="fcs-input-phone" />
                </Field>
                <Field id="email" label="Email *">
                  <Input id="email" type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="fcs-input-email" />
                </Field>
              </div>

              <Field id="business_description" label="What does your business do? *" hint="A sentence or two is plenty. For example: event planning and promotions for festivals and corporate events.">
                <Textarea id="business_description" required rows={3} value={form.business_description} onChange={set("business_description")} className="rounded-none bg-[#FFFDF8] border-[#D8D1C2] text-[#1C2430]" data-testid="fcs-input-description" />
              </Field>

              <Field id="sam" label="Is your business registered on SAM.gov? *">
                <ChoiceGroup name="sam" options={SAM_OPTIONS} value={form.sam_status} onChange={set("sam_status")} />
              </Field>

              <Field id="goal" label="What's your main goal? *">
                <ChoiceGroup name="goal" options={GOAL_OPTIONS} value={form.main_goal} onChange={set("main_goal")} />
              </Field>

              <div className="grid sm:grid-cols-2 gap-6">
                <Field id="best_time" label="Best time to call">
                  <Input id="best_time" placeholder="e.g. weekday mornings" value={form.best_time} onChange={set("best_time")} className={inputCls} data-testid="fcs-input-best-time" />
                </Field>
                <Field id="referred_by" label="Who referred you?">
                  <Input id="referred_by" placeholder="Name of the person who sent you" value={form.referred_by} onChange={set("referred_by")} className={inputCls} data-testid="fcs-input-referred-by" />
                </Field>
              </div>

              {error && <p className="text-sm font-medium text-[#9B2C2C]" data-testid="fcs-intake-error">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                data-testid="fcs-intake-submit"
                className="group inline-flex items-center gap-3 px-8 h-12 font-semibold transition-opacity duration-200 disabled:opacity-60"
                style={{ background: C.navy, color: C.cream }}
              >
                {submitting ? <SpinnerGap className="animate-spin" size={18} /> : null}
                {submitting ? "Sending..." : "Send my information"}
                {!submitting && <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />}
              </button>
            </form>
          )}
        </main>
      </div>
    </div>
  );
}
